import { isNonNullable } from 'lib/array/arrayUtils'
import {
  extractCancellationFromFinePrint,
  extractDateChangesFromFinePrint,
  extractInclusionNotesFromFinePrint,
} from 'components/OfferPage/ThingsToKnow/finePrintUtils'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { isLEHotel } from 'lib/offer/offerTypes'
import {
  displayContextBody,
  displayContextField,
  nonEmptyString,
} from './offerPageContextFormatting'
import type {
  AccommodationOfferPageAssistantFinePrintSectionRequest,
} from './offerPageContextTypes'

function buildFinePrintSection(
  title: string | undefined,
  body: string | undefined | null,
): AccommodationOfferPageAssistantFinePrintSectionRequest | undefined {
  const normalizedBody = displayContextBody(body)
  if (!normalizedBody) {
    return undefined
  }

  const normalizedTitle = displayContextField(title)
  return {
    ...(normalizedTitle ? { title: normalizedTitle } : {}),
    body: normalizedBody,
  }
}

function buildLeHotelFinePrintSections(offer: App.HotelOffer | undefined): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  if (!offer) {
    return []
  }

  const { cancellation, remaining: finePrintAfterCancellation } = extractCancellationFromFinePrint(offer.finePrint)
  const { remaining: finePrintAfterInclusions } = extractInclusionNotesFromFinePrint(finePrintAfterCancellation)
  const { dateChanges, remaining: remainingFinePrint } = extractDateChangesFromFinePrint(finePrintAfterInclusions)

  return [
    buildFinePrintSection('Cancellation policy', cancellation),
    buildFinePrintSection('Date changes', dateChanges),
    buildFinePrintSection('Taxes & fees', offer.property?.taxesAndFeesContent),
    buildFinePrintSection('Fine print', remainingFinePrint),
  ].filter(isNonNullable)
}

function buildBedbankFinePrintSections(offer: App.BedbankOffer | undefined): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  const finePrint = offer?.property.finePrint
  if (!offer) {
    return []
  }

  const promotionCancellation = offer.promotions
    .map(promotion => nonEmptyString(promotion.cancellationPolicy))
    .filter(isNonNullable)
    .join('\n\n')
  const promotionFinePrint = offer.promotions.find(promotion => nonEmptyString(promotion.finePrint))?.finePrint
  const checkIn = [
    finePrint?.checkIn?.beginTime ? `Check-in starts at ${finePrint.checkIn.beginTime}` : undefined,
    finePrint?.checkIn?.endTime ? `Check-in ends at ${finePrint.checkIn.endTime}` : undefined,
  ].filter(isNonNullable).join('\n')
  const checkInInstructions = [
    finePrint?.checkIn?.instructions,
    finePrint?.checkIn?.specialInstructions,
  ].map(value => nonEmptyString(value)).filter(isNonNullable).join('\n\n')
  const fees = [
    finePrint?.fees?.mandatory,
    finePrint?.fees?.optional,
  ].map(value => nonEmptyString(value)).filter(isNonNullable).join('\n\n')
  const policies = finePrint?.policies ?
      Object.entries(finePrint.policies)
        .map(([key, value]) => buildFinePrintSection(key, value))
        .filter(isNonNullable) :
      []

  return [
    buildFinePrintSection('Check-in', checkIn),
    buildFinePrintSection('Check-out', finePrint?.checkOut?.time),
    buildFinePrintSection('Check-in instructions', checkInInstructions),
    buildFinePrintSection('Fees', fees),
    buildFinePrintSection('Pets', finePrint?.pets?.join('\n')),
    buildFinePrintSection('General', finePrint?.general?.join('\n')),
    buildFinePrintSection('Cleaning and safety', finePrint?.safety?.join('\n')),
    ...policies,
    buildFinePrintSection('Cancellation policy', promotionCancellation),
    buildFinePrintSection('Fine print', offer.property.finePrintOverride ?? promotionFinePrint),
  ].filter(isNonNullable)
}

function buildPropertyFinePrintSections(property: App.Property.Property | undefined): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  if (!property) {
    return []
  }

  const subsectionRows = property.finePrintSubsections?.map(row => buildFinePrintSection(row.label, row.content.value)) ?? []
  const structuredFinePrint = property.structuredFinePrint

  return [
    ...subsectionRows,
    buildFinePrintSection('Fine print', property.finePrint),
    buildFinePrintSection('Taxes & fees', property.taxesAndFeesContent),
    buildFinePrintSection('Check-in', structuredFinePrint?.checkIn?.beginTime),
    buildFinePrintSection('Check-out', structuredFinePrint?.checkOut?.time),
    buildFinePrintSection('Fees', [
      structuredFinePrint?.fees?.mandatory,
      structuredFinePrint?.fees?.optional,
    ].map(value => nonEmptyString(value)).filter(isNonNullable).join('\n\n')),
  ].filter(isNonNullable)
}

export function buildOfferPageFinePrintSections(
  offerPageState: OfferPageState,
): Array<AccommodationOfferPageAssistantFinePrintSectionRequest> {
  if (offerPageState.property) {
    return buildPropertyFinePrintSections(offerPageState.property)
  }

  return [
    ...buildLeHotelFinePrintSections(isLEHotel(offerPageState.offer) ? offerPageState.offer : undefined),
    ...buildBedbankFinePrintSections(offerPageState.bedbankOffer),
  ]
}
